import { Outlet, useLocation, useNavigate } from 'react-router';
import { ScrollToTop } from '../components/ScrollToTop';
import { GoogleTagRouteListener } from '../components/GoogleTagRouteListener';
import { AuthProvider } from '../lib/auth-context';
import { AdaptiveExperienceProvider } from '../context/AdaptiveExperienceContext';
import { ThemeProvider } from '../lib/theme-provider';
import { AppSettingsProvider } from '../lib/app-settings-context';
import { AppLayout } from '../components/AppLayout';
import { SiteFooter } from '../components/SiteFooter';
import { SiteBackNav } from '../components/SiteBackNav';
import { Toaster } from '../components/ui/sonner';
import { BetaFeedbackWidget } from '../components/BetaFeedbackWidget';
import { OnboardingWrapper } from '../components/OnboardingWrapper';
import { Component, ReactNode } from 'react';
import { useEffect } from 'react';
import { EnterpriseOrgProvider } from '../context/EnterpriseOrgContext';
import { CloudSyncAutoManager } from '../components/CloudSyncAutoManager';
import { RelationshipIntelligenceProvider } from '../context/RelationshipIntelligenceContext';
import { isNativeConsumerShell, isRestrictedInNativeConsumerShell } from '../lib/consumer-shell-guards';

// Error boundary to catch Figma errors at the route level
class RouteErrorCatcher extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    const errorStr = (error.message + (error.stack || '')).toLowerCase();
    const isFigmaError =
      errorStr.includes('illegal constructor') ||
      errorStr.includes('messageport') ||
      errorStr.includes('messagechannel') ||
      errorStr.includes('figma');

    // Only set error state for real errors
    return { hasError: !isFigmaError };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }) {
    const errorStr = (error.message + (error.stack || '')).toLowerCase();
    const isFigmaError =
      errorStr.includes('illegal constructor') ||
      errorStr.includes('messageport') ||
      errorStr.includes('messagechannel') ||
      errorStr.includes('figma');

    if (isFigmaError) {
      this.setState({ hasError: false });
      return;
    }
    // Log component stack to track React #130 / object-as-child errors
    if (errorInfo?.componentStack) {
      console.error('🔍 RouteErrorCatcher component stack:', errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 bg-slate-950">
          <div className="max-w-md w-full text-center space-y-4">
            <p className="text-lg font-semibold text-white">
              Something went wrong on this page.
            </p>
            <p className="text-sm text-slate-300">
              Try refreshing or go back to home. If it keeps happening, sign out and sign in again.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                type="button"
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
              >
                Go to home
              </button>
              <button
                type="button"
                onClick={() => this.setState({ hasError: false })}
                className="px-4 py-2 border border-slate-600 bg-slate-800 text-slate-100 rounded-lg hover:bg-slate-700"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Root component - handles auth context and app layout
export default function Root() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeConsumerShell()) return;
    const path = location.pathname || '/';
    // Consumer native app must not expose admin/control-plane surfaces.
    if (isRestrictedInNativeConsumerShell(path)) {
      navigate('/home', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <AppSettingsProvider>
      <AuthProvider>
        <EnterpriseOrgProvider>
          <AdaptiveExperienceProvider>
          <RelationshipIntelligenceProvider>
          <ThemeProvider defaultTheme="light" storageKey="tension-theme">
            <GoogleTagRouteListener />
            <Toaster />
            <CloudSyncAutoManager />
            <OnboardingWrapper>
              <div className="min-h-screen flex flex-col">
                <AppLayout>
                  <SiteBackNav />
                  <div id="main-content" tabIndex={-1} className="flex-1">
                    <RouteErrorCatcher>
                      <ScrollToTop />
                      <Outlet />
                    </RouteErrorCatcher>
                  </div>
                  <NamedRootBetaFeedbackBoundary>
                    <BetaFeedbackWidget />
                  </NamedRootBetaFeedbackBoundary>
                </AppLayout>
                <SiteFooter />
              </div>
            </OnboardingWrapper>
          </ThemeProvider>
          </RelationshipIntelligenceProvider>
          </AdaptiveExperienceProvider>
        </EnterpriseOrgProvider>
      </AuthProvider>
    </AppSettingsProvider>
  );
}

class NamedRootBetaFeedbackBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('🚨 BetaFeedbackWidget render error', error);
    if (errorInfo?.componentStack) {
      console.error('BetaFeedbackWidget component stack:', errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}